import { Button, Spinner } from "@design";
import { Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { ApiError } from "@/api";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { DangerZone } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { useAdminCanEdit } from "../useAdminCanEdit";
import { ConfigureModelForm } from "./ConfigureModelForm";
import { ModelSummary } from "./ModelSummary";
import { useModelActions } from "./useModelActions";
import { useAdminModel, useAdminModels } from "./useModelData";

/** /admin/models/:modelId: configure a model (full page), with delete in a danger zone. */
export function ModelDetailPage() {
  const { t } = useTranslation("adminModels");
  const areas = useAreaCrumbs();
  const { modelId } = useParams();
  const navigate = useNavigate();
  const canEdit = useAdminCanEdit();
  const list = useAdminModels();
  const single = useAdminModel(modelId ?? "");
  const models = list.data?.data ?? [];
  const model = single.data?.data;
  const actions = useModelActions(models);
  const back = () => navigate("/admin/models");

  let body: ReactNode;
  const notFound = single.error instanceof ApiError && single.error.status === 404;
  if (single.isPending || list.isPending) body = <Spinner />;
  else if (!notFound && (single.isError || list.isError)) {
    body = (
      <Text tone="error" role="alert">
        {t("loadFailed")}
      </Text>
    );
  } else if (!model) {
    body = (
      <Text tone="secondary" role="alert">
        {t("detail.notFound")}
      </Text>
    );
  } else if (!canEdit) body = <ModelSummary model={model} />;
  else {
    body = (
      <>
        <ConfigureModelForm
          key={model.updatedAt}
          model={model}
          models={models}
          confirmDisable={() => actions.confirmReassign(model, "disable")}
        />
        <DangerZone title={t("detail.danger.title")} description={t("detail.danger.description")}>
          <div>
            <Button
              variant="danger"
              leadingIcon={<Trash2 size={16} />}
              loading={actions.deleting}
              onClick={() => void actions.deleteModel(model, back)}
            >
              {t("detail.danger.delete")}
            </Button>
          </div>
        </DangerZone>
      </>
    );
  }

  return (
    <div className="flex flex-col">
      <PageBreadcrumbs
        trail={[areas.admin, { label: t("title"), to: "/admin/models" }]}
        current={model?.name ?? modelId ?? ""}
      />
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-4 py-6 md:px-8">
        {!canEdit && (
          <Text variant="caption" tone="secondary">
            {t("detail.readOnly")}
          </Text>
        )}
        {body}
      </div>
      {actions.dialog}
    </div>
  );
}
